import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Select } from '../atoms';
import { useToast } from './Toast';

/**
 * NotificationMatrixEditor — the super-admin permission grid for feature 41.
 *
 * A grid mapping each notification EVENT (row) → WHO (assignee / follower / firm
 * admin) → CHANNEL (in-app / email; push is rendered disabled — "coming soon") +
 * a per-event urgency. Reads the merged full matrix from
 * GET /api/org-settings/notification-matrix and persists edits via PUT (super_admin
 * only, enforced server-side). Backed by config/notification-matrix.js — the FE
 * renders whatever event rows the server returns, so a new backend event surfaces
 * automatically (humanized fallback label).
 */

type Urgency = 'low' | 'normal' | 'high' | 'critical';

interface MatrixRow {
  urgency: Urgency;
  audiences: string[];
  channels: { in_app: boolean; email: boolean };
}
type Matrix = Record<string, MatrixRow>;

// FE display metadata (mirrors config/notification-matrix.js). Event rows that
// aren't listed here still render with a humanized label + default order 99.
const EVENT_META: Record<string, { label: string; order: number }> = {
  appeal_filed: { label: 'Appeal filed', order: 1 },
  appeal_stage_advanced: { label: 'Appeal stage advanced', order: 2 },
  appeal_outcome: { label: 'Appeal outcome recorded', order: 3 },
  bill_status_changed: { label: 'Tax-bill status changed', order: 4 },
  invoice_status_changed: { label: 'Invoice sent', order: 5 },
  invoice_paid: { label: 'Invoice paid', order: 6 },
  document_uploaded: { label: 'Document uploaded', order: 7 },
  document_shared: { label: 'Document shared', order: 8 },
  document_unshared: { label: 'Document unshared', order: 9 },
  // Grouped with the other document rows: this is the "they opened what I sent" signal.
  // Only the FIRST view raises it — repeat views stay in the activity feed, which is why
  // there is no row here for them to configure.
  client_document_first_viewed: { label: 'Client first opened a document', order: 9.5 },
  client_created: { label: 'Client onboarded', order: 10 },
  property_created: { label: 'Property added', order: 11 },
  parcel_created: { label: 'Parcel added', order: 12 },
  note_added: { label: 'Note added', order: 13 },
};

const AUDIENCES: { key: string; label: string }[] = [
  { key: 'assignee', label: 'Assignees' },
  { key: 'follower', label: 'Followers' },
  { key: 'admin', label: 'Firm admins' },
];

const URGENCY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function humanize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A bare grid-cell checkbox (matches PermissionsEditor's house style). */
function GridCheckbox({ checked, disabled, onChange, ariaLabel }: {
  checked: boolean; disabled: boolean; onChange: (on: boolean) => void; ariaLabel: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={ariaLabel}
      style={{ width: 16, height: 16, accentColor: 'var(--brand-primary)', cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  );
}

function labelFor(eventType: string): string {
  return EVENT_META[eventType]?.label ?? humanize(eventType);
}

function orderOf(eventType: string): number {
  return EVENT_META[eventType]?.order ?? 99;
}

/** Deep-ish clone so the draft never aliases the query cache. */
function cloneMatrix(m: Matrix): Matrix {
  const out: Matrix = {};
  for (const [k, r] of Object.entries(m)) {
    out[k] = { urgency: r.urgency, audiences: [...r.audiences], channels: { ...r.channels } };
  }
  return out;
}

function matrixEqual(a: Matrix, b: Matrix): boolean {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length || ak.some((k, i) => k !== bk[i])) return false;
  for (const k of ak) {
    const ra = a[k], rb = b[k];
    if (ra.urgency !== rb.urgency) return false;
    if (ra.channels.in_app !== rb.channels.in_app || ra.channels.email !== rb.channels.email) return false;
    const sa = [...ra.audiences].sort().join(',');
    const sb = [...rb.audiences].sort().join(',');
    if (sa !== sb) return false;
  }
  return true;
}

export function NotificationMatrixEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const matrixQuery = useQuery<Matrix>({
    queryKey: ['org-settings', 'notification-matrix'],
    queryFn: ({ signal }) => api.get<Matrix>('/api/org-settings/notification-matrix', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<Matrix | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = matrixQuery.data;
  const value = draft ?? persisted ?? null;
  const dirty = draft !== null && persisted != null && !matrixEqual(draft, persisted);

  const eventTypes = useMemo(
    () => (value ? Object.keys(value).sort((a, b) => orderOf(a) - orderOf(b) || a.localeCompare(b)) : []),
    [value],
  );

  function edit(eventType: string, patch: Partial<MatrixRow>) {
    if (!value) return;
    const base = cloneMatrix(value);
    base[eventType] = { ...base[eventType], ...patch };
    setDraft(base);
  }

  function toggleAudience(eventType: string, audience: string, on: boolean) {
    if (!value) return;
    const current = value[eventType].audiences;
    const next = on ? Array.from(new Set([...current, audience])) : current.filter((a) => a !== audience);
    edit(eventType, { audiences: next });
  }

  function toggleChannel(eventType: string, channel: 'in_app' | 'email', on: boolean) {
    if (!value) return;
    edit(eventType, { channels: { ...value[eventType].channels, [channel]: on } });
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/notification-matrix', { notification_matrix: draft });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'notification-matrix'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Notification matrix saved.' });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  const headCell: React.CSSProperties = {
    padding: 'var(--sp-2) var(--sp-3)',
    fontSize: 'var(--fs-xs)',
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--color-ink-3)',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--color-border)',
  };
  const cell: React.CSSProperties = {
    padding: 'var(--sp-2) var(--sp-3)',
    textAlign: 'center',
    borderBottom: '1px solid var(--color-border)',
    verticalAlign: 'middle',
  };

  return (
    <div style={{
      gridColumn: '1 / -1',
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Notification matrix</h3>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          {canEdit && dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={saving}>Reset</Button>
              <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        For each event, choose who is notified and on which channels. In-app writes a notification;
        email sends when configured. Push is coming soon. {canEdit ? '' : 'Editable by a super_admin.'}
      </p>

      {matrixQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr>
                <th style={{ ...headCell, textAlign: 'left' }}>Event</th>
                {AUDIENCES.map((a) => <th key={a.key} style={headCell}>{a.label}</th>)}
                <th style={headCell}>In-app</th>
                <th style={headCell}>Email</th>
                <th style={headCell}>Push</th>
                <th style={headCell}>Urgency</th>
              </tr>
            </thead>
            <tbody>
              {eventTypes.map((et) => {
                const row = value[et];
                return (
                  <tr key={et}>
                    <td style={{ ...cell, textAlign: 'left', color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
                      {labelFor(et)}
                    </td>
                    {AUDIENCES.map((a) => (
                      <td key={a.key} style={cell}>
                        <GridCheckbox
                          checked={row.audiences.includes(a.key)}
                          disabled={!canEdit}
                          onChange={(on) => toggleAudience(et, a.key, on)}
                          ariaLabel={`${labelFor(et)} — ${a.label}`}
                        />
                      </td>
                    ))}
                    <td style={cell}>
                      <GridCheckbox
                        checked={row.channels.in_app}
                        disabled={!canEdit}
                        onChange={(on) => toggleChannel(et, 'in_app', on)}
                        ariaLabel={`${labelFor(et)} — In-app`}
                      />
                    </td>
                    <td style={cell}>
                      <GridCheckbox
                        checked={row.channels.email}
                        disabled={!canEdit}
                        onChange={(on) => toggleChannel(et, 'email', on)}
                        ariaLabel={`${labelFor(et)} — Email`}
                      />
                    </td>
                    <td style={cell}>
                      <span
                        title="Push notifications are coming soon"
                        style={{
                          display: 'inline-block',
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--color-muted)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--r-sm)',
                          padding: '1px 6px',
                          opacity: 0.7,
                        }}
                      >Soon</span>
                    </td>
                    <td style={cell}>
                      <div style={{ minWidth: 110, margin: '0 auto' }}>
                        <Select
                          size="sm"
                          options={URGENCY_OPTIONS}
                          value={row.urgency}
                          disabled={!canEdit}
                          onChange={(e) => edit(et, { urgency: e.target.value as Urgency })}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
